/**
 * SandboxScene - Free play scene
 */

import { useState, useEffect } from 'react';

export default function SandboxScene({ level, isComplete, isListening }) {
  const [items] = useState(['🎈', '⭐', '🎾', '🪁']);
  const [activeItem, setActiveItem] = useState(0);

  useEffect(() => {
    if (isComplete) {
      setActiveItem(prev => (prev + 1) % items.length);
    }
  }, [isComplete, items.length]);

  return (
    <div style={styles.scene}>
      {/* Playground background */}
      <div style={styles.playground}>
        <div style={styles.sky} />
        <div style={styles.ground} />
        {/* Sun */}
        <div style={styles.sun}>☀️</div>
      </div>

      {/* Interactive items */}
      <div style={styles.itemsContainer}>
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              ...styles.item,
              animationDelay: `${i * 0.5}s`,
              transform: i === activeItem ? 'scale(1.3)' : 'scale(1)',
              opacity: i === activeItem ? 1 : 0.6,
            }}
          >
            {item}
          </div>
        ))}
      </div>

      {/* Listening indicator */}
      {isListening && (
        <div style={styles.listening}>
          <div style={styles.wave} />
          <div style={{ ...styles.wave, animationDelay: '0.2s' }} />
          <div style={{ ...styles.wave, animationDelay: '0.4s' }} />
        </div>
      )}

      {/* Status */}
      <div style={styles.status}>
        {level?.sceneConfig?.playground 
          ? 'Say anything to play!'
          : 'Tell the toy what to do!'
        }
      </div>
    </div>
  );
}

const styles = {
  scene: { position: 'absolute', inset: 0, overflow: 'hidden' },
  playground: { position: 'absolute', inset: 0 },
  sky: { position: 'absolute', top: 0, left: 0, right: 0, height: '65%', background: 'linear-gradient(180deg, #38bdf8 0%, #bae6fd 100%)' },
  ground: { position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', background: 'linear-gradient(180deg, #fde68a 0%, #fbbf24 100%)' },
  sun: { position: 'absolute', top: '10%', right: '15%', fontSize: '60px', animation: 'sunPulse 3s ease-in-out infinite' },
  itemsContainer: { position: 'absolute', top: '35%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', gap: '30px' },
  item: { fontSize: '60px', animation: 'itemFloat 3s ease-in-out infinite', transition: 'all 0.3s ease', cursor: 'pointer' },
  listening: { position: 'absolute', bottom: '25%', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '5px', alignItems: 'flex-end' },
  wave: { width: '8px', height: '30px', background: '#22c55e', borderRadius: '4px', animation: 'waveAnim 0.5s ease-in-out infinite alternate' },
  status: { position: 'absolute', bottom: '12%', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', color: 'white', padding: '12px 24px', borderRadius: '30px', fontFamily: "'Fredoka', sans-serif", fontSize: '16px', fontWeight: 500 },
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes itemFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
  @keyframes sunPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
  @keyframes waveAnim { 0% { height: 10px; } 100% { height: 40px; } }
`;
if (!document.querySelector('#sandbox-scene-styles')) {
  styleSheet.id = 'sandbox-scene-styles';
  document.head.appendChild(styleSheet);
}
